import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Send,
  Check,
  Calendar,
  CheckCircle2,
} from "lucide-react";
import {
  getReferralsSent,
  closeReferral,
  cancelReferral,
  markAppointmentScheduled,
} from "../APIs/referralApi";
import { getPatientProfile } from "../APIs/activePatientApi";
import ReferralDocumentsSection from "./ReferralDocumentsSection";

const STATUS_CONFIG = {
  PENDING_ACCEPTANCE: {
    label: "Awaiting Specialist",
    color: "bg-yellow-100 text-yellow-700",
  },
  ACCEPTED: { label: "Accepted", color: "bg-blue-100 text-blue-700" },
  APPOINTMENT_OFFERED: {
    label: "Slots Offered to Patient",
    color: "bg-blue-100 text-blue-700",
  },
  PATIENT_CHOICE: {
    label: "Awaiting Patient Choice",
    color: "bg-purple-100 text-purple-700",
  },
  SCHEDULED: { label: "Booked", color: "bg-green-100 text-green-700" },
  COMPLETED: { label: "Visit Completed", color: "bg-gray-100 text-gray-600" },
  MANUAL_REVIEW: {
    label: "Under Review",
    color: "bg-orange-100 text-orange-700",
  },
  CLOSED: { label: "Closed", color: "bg-gray-100 text-gray-400" },
  DECLINED: { label: "Declined", color: "bg-red-100 text-red-600" },
  REMATCHED: { label: "Re-matched", color: "bg-indigo-100 text-indigo-700" },
  CANCELLED: { label: "Cancelled", color: "bg-red-100 text-red-700" },
  EXTERNAL_PENDING: {
    label: "Fax Sent",
    color: "bg-yellow-100 text-yellow-700",
  },
  EXTERNAL_SENT: { label: "Fax Delivered", color: "bg-blue-100 text-blue-700" },
  EXTERNAL_ACCEPTED: {
    label: "Accepted",
    color: "bg-green-100 text-green-700",
  },
  EXTERNAL_DECLINED: { label: "Declined", color: "bg-red-100 text-red-600" },
  EXTERNAL_FAX_FAILED: {
    label: "Fax Failed",
    color: "bg-orange-100 text-orange-700",
  },
};

const URGENCY_LABEL = {
  ROUTINE: "Routine",
  WITHIN_30_DAYS: "Within 30 Days",
  WITHIN_7_DAYS: "Within 7 Days",
  WITHIN_3_DAYS: "Within 3 Days",
};

const ROUTING_LABEL = {
  SMART: "Smart",
  PROVIDER_DIRECTED: "Provider Directed",
  PATIENT_CHOICE: "Patient Choice",
  EXTERNAL: "External (Fax)",
};

const CANCELLABLE_STATUSES = new Set([
  "SUBMITTED",
  "MATCHING",
  "PENDING_PATIENT_SELECTION",
  "PENDING_ACCEPTANCE",
  "INFO_REQUESTED",
  "MANUAL_REVIEW",
]);

const RESOLVED_STATUSES = new Set(["SCHEDULED", "COMPLETED", "CLOSED"]);

const isDeclinedReferral = (r) =>
  !RESOLVED_STATUSES.has(r.status) &&
  (r.status === "DECLINED" ||
    r.matchAttempts?.some((a) => a.status === "DECLINED"));

const declineMessage = (r) => {
  const lastDecline = r.matchAttempts
    ?.slice()
    .reverse()
    .find((a) => a.status === "DECLINED");
  const declinedBy = lastDecline?.specialistName
    ? `Dr. ${lastDecline.specialistName}`
    : "Specialist";
  if (r.routingMode === "PROVIDER_DIRECTED" && r.status === "DECLINED")
    return `${declinedBy} declined this referral`;
  if (r.routingMode === "SMART") {
    if (r.status === "PENDING_ACCEPTANCE") {
      const newSpec = r.specialistName
        ? `Re-matched to Dr. ${r.specialistName}`
        : "Re-matching in progress";
      return `${declinedBy} declined — ${newSpec} (pending acceptance)`;
    }
    if (r.status === "MANUAL_REVIEW")
      return `${declinedBy} declined — No more candidates found`;
  }
  if (
    r.routingMode === "PATIENT_CHOICE" &&
    r.status === "PENDING_PATIENT_SELECTION"
  )
    return `${declinedBy} declined — Patient re-selecting`;
  return `${declinedBy} declined`;
};

const EXTERNAL_STATUS_TEXT = {
  EXTERNAL_ACCEPTED: "Accepted",
  EXTERNAL_DECLINED: "Declined",
  EXTERNAL_FAX_FAILED: "Fax delivery failed",
  EXTERNAL_SENT: "Fax delivered — awaiting response",
  EXTERNAL_PENDING: "Awaiting response",
};

const matchSubtitle = (r) => {
  if (r.routingMode === "EXTERNAL") {
    const statusText = EXTERNAL_STATUS_TEXT[r.status] ?? "Awaiting response";
    if (r.externalProviderName)
      return `Sent to Dr. ${r.externalProviderName} — ${statusText}`;
    return statusText;
  }
  if (r.specialistName) return `Assigned to Dr. ${r.specialistName}`;
  if (r.routingMode === "PATIENT_CHOICE") return "Awaiting patient choice";
  if (r.status === "SCHEDULED") return "Appointment scheduled";
  if (r.status === "COMPLETED") return "Visit completed";
  if (r.status === "CLOSED") return "Referral closed";
  if (r.status === "DECLINED") return "Specialist declined";
  return "Matching in progress";
};

// ── Progress bar ──────────────────────────────────────────────────────────────

const getStepIndex = (r) => {
  switch (r.status) {
    case "COMPLETED":
    case "CLOSED":
      return 3;
    case "SCHEDULED":
      return 2;
    case "ACCEPTED":
    case "APPOINTMENT_OFFERED":
    case "EXTERNAL_ACCEPTED":
      return 1;
    default:
      return 0;
  }
};

const PROGRESS_STEPS = [
  { label: "Sent", Icon: Send },
  { label: "Accepted", Icon: Check },
  { label: "Appt Scheduled", Icon: Calendar },
  { label: "Completed", Icon: CheckCircle2 },
];

function ReferralProgressBar({ referral }) {
  const stepIndex = getStepIndex(referral);
  const isDeclined =
    referral.status === "DECLINED" || referral.status === "EXTERNAL_DECLINED";
  const fillPct = Math.round((stepIndex / 3) * 100);
  const barColor = isDeclined ? "bg-red-500" : "bg-green-600";
  const doneStyle = isDeclined
    ? "bg-red-500 border-red-500 text-white"
    : "bg-green-600 border-green-600 text-white";

  return (
    <div className="mt-3 select-none">
      <div className="flex items-center gap-3 mb-2">
        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${barColor} transition-all duration-300`}
            style={{ width: `${fillPct}%` }}
          />
        </div>
        {isDeclined ? (
          <span className="text-xs text-red-500 whitespace-nowrap shrink-0 font-medium">
            Declined
          </span>
        ) : (
          <span className="text-xs text-gray-500 whitespace-nowrap shrink-0">
            Step {stepIndex + 1} of 4
          </span>
        )}
      </div>

      <div className="flex justify-between">
        {PROGRESS_STEPS.map(({ label, Icon }, i) => {
          const done = i <= stepIndex;
          return (
            <div key={i} className="flex flex-col items-center gap-0.5 flex-1">
              <div
                className={`w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0
                  ${done ? doneStyle : "border-gray-300 text-gray-300 bg-white"}`}
              >
                <Icon size={13} />
              </div>
              <span
                className={`text-xs text-center leading-tight mt-0.5
                  ${done ? (isDeclined ? "text-red-500" : "text-gray-700") : "text-gray-400"}`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ReferralsSentTab({
  providerId,
  statusFilter,
  declinedView,
  cancelledView,
  onResend,
}) {
  const [referrals, setReferrals] = useState([]);
  const [patientNames, setPatientNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [confirming, setConfirming] = useState({});
  const [closing, setClosing] = useState({});
  const [cancelReason, setCancelReason] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [markingScheduled, setMarkingScheduled] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReferralsSent(providerId);
      const list = Array.isArray(data) ? data : (data.content ?? []);
      setReferrals(list);

      const missingIds = [
        ...new Set(
          list
            .filter((r) => !r.patientName && r.patientId)
            .map((r) => r.patientId),
        ),
      ];
      if (missingIds.length > 0) {
        const results = await Promise.allSettled(
          missingIds.map((id) => getPatientProfile(id)),
        );
        const nameMap = {};
        results.forEach((res, i) => {
          if (res.status === "fulfilled" && res.value) {
            const p = res.value;
            const full = [p.firstName, p.lastName].filter(Boolean).join(" ");
            if (full) nameMap[missingIds[i]] = full;
          }
        });
        setPatientNames(nameMap);
      }
    } catch (err) {
      console.error("Failed to load sent referrals:", err);
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    load();
  }, [load]);

  const getPatientName = (r) =>
    r.patientName || patientNames[r.patientId] || `Patient #${r.patientId}`;

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const handleClose = async (referralId) => {
    setClosing((c) => ({ ...c, [referralId]: true }));
    setConfirming((c) => ({ ...c, [referralId]: false }));
    try {
      await closeReferral(referralId);
      await load();
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to close referral",
      );
    } finally {
      setClosing((c) => ({ ...c, [referralId]: false }));
    }
  };

  const handleCancel = async (referralId) => {
    if (!cancelReason.trim()) return;
    setClosing((c) => ({ ...c, [referralId]: true }));
    try {
      await cancelReferral(referralId, cancelReason.trim());
      setCancelReason("");
      setConfirming((c) => ({ ...c, [referralId]: false }));
      await load();
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to cancel referral",
      );
    } finally {
      setClosing((c) => ({ ...c, [referralId]: false }));
    }
  };

  const handleMarkScheduled = async (referralId) => {
    setMarkingScheduled((m) => ({ ...m, [referralId]: true }));
    try {
      await markAppointmentScheduled(referralId);
      await load();
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to mark as scheduled",
      );
    } finally {
      setMarkingScheduled((m) => ({ ...m, [referralId]: false }));
    }
  };

  if (loading)
    return (
      <p className="text-sm text-gray-500 py-6 text-center">
        Loading sent referrals...
      </p>
    );

  if (referrals.length === 0)
    return (
      <div className="py-12 text-center text-gray-500">
        <p>No sent referrals yet.</p>
        <p className="text-sm mt-1">Click "New Referral" to create one.</p>
      </div>
    );

  const closedReferrals =
    statusFilter || declinedView || cancelledView
      ? []
      : referrals.filter((r) => r.status === "CLOSED");

  const visibleReferrals = cancelledView
    ? referrals.filter((r) => r.status === "CANCELLED")
    : declinedView
      ? referrals.filter(isDeclinedReferral)
      : statusFilter
        ? referrals.filter((r) => statusFilter.includes(r.status))
        : showClosed
          ? referrals.filter(
              (r) => r.status !== "DECLINED" && r.status !== "CANCELLED",
            )
          : referrals.filter(
              (r) =>
                r.status !== "CLOSED" &&
                r.status !== "DECLINED" &&
                r.status !== "CANCELLED",
            );

  return (
    <div className="space-y-3">
      {visibleReferrals.map((r) => {
        const status = STATUS_CONFIG[r.status] ?? {
          label: r.status,
          color: "bg-gray-100 text-gray-600",
        };
        const isExpanded = expanded[r.referralId];
        const attempts = r.matchAttempts ?? [];
        const isInactive = r.status === "CLOSED" || r.status === "DECLINED";
        const canCancel = CANCELLABLE_STATUSES.has(r.status);
        const canClose = r.status === "COMPLETED";
        const isConfirming = confirming[r.referralId];
        const isClosing = closing[r.referralId];
        const canMarkScheduled =
          r.routingMode === "EXTERNAL" && r.status === "EXTERNAL_ACCEPTED";
        const isMarkingScheduled = markingScheduled[r.referralId];

        return (
          <div
            key={r.referralId}
            className={`rounded-xl border bg-white shadow-sm overflow-hidden${isInactive ? " opacity-60" : ""}`}
          >
            {/* Card header */}
            <button
              className="w-full text-left p-4 hover:shadow-md transition"
              onClick={() => toggleExpand(r.referralId)}
            >
              <div className="flex-1 min-w-0">
                {/* Top row: name + badge */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-gray-900 truncate">
                      {getPatientName(r)}
                    </div>
                    {r.specialty && (
                      <div className="text-sm text-gray-600">{r.specialty}</div>
                    )}
                    <div className="text-sm text-gray-500">
                      {matchSubtitle(r)}
                    </div>
                    {r.reason && (
                      <div className="text-sm text-gray-500 truncate">
                        Reason: {r.reason}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}
                    >
                      {status.label}
                    </span>
                    <div className="text-gray-400 mt-1">
                      {isExpanded ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <ReferralProgressBar referral={r} />
              </div>
            </button>

            {/* Expanded detail */}
            {isExpanded && (
              <div className="border-t bg-gray-50 px-4 py-4 space-y-3">
                <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                  {r.routingMode === "EXTERNAL" && r.externalProviderName && (
                    <>
                      <span className="text-gray-500 font-medium whitespace-nowrap">
                        Sent to
                      </span>
                      <span className="text-gray-800">
                        Dr. {r.externalProviderName}
                      </span>
                    </>
                  )}
                  {r.routingMode === "EXTERNAL" && r.faxNumber && (
                    <>
                      <span className="text-gray-500 font-medium whitespace-nowrap">
                        Fax number
                      </span>
                      <span className="text-gray-800">{r.faxNumber}</span>
                    </>
                  )}
                  {r.routingMode === "EXTERNAL" && r.shortClaimCode && (
                    <>
                      <span className="text-gray-500 font-medium whitespace-nowrap">
                        Referral code
                      </span>
                      <span className="text-gray-800 font-mono tracking-wider">
                        {r.shortClaimCode}
                      </span>
                    </>
                  )}
                  {(r.locationCity || r.locationState) && (
                    <>
                      <span className="text-gray-500 font-medium whitespace-nowrap">
                        Location
                      </span>
                      <span className="text-gray-800">
                        {[r.locationCity, r.locationState]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </>
                  )}
                  {r.insurancePlanSnapshot && (
                    <>
                      <span className="text-gray-500 font-medium whitespace-nowrap">
                        Insurance
                      </span>
                      <span className="text-gray-800">
                        {r.insurancePlanSnapshot}
                      </span>
                    </>
                  )}
                  {r.routingMode && (
                    <>
                      <span className="text-gray-500 font-medium whitespace-nowrap">
                        Routing
                      </span>
                      <span className="text-gray-800">
                        {ROUTING_LABEL[r.routingMode] ?? r.routingMode}
                      </span>
                    </>
                  )}
                  {r.urgency && (
                    <>
                      <span className="text-gray-500 font-medium whitespace-nowrap">
                        Urgency
                      </span>
                      <span className="text-gray-800">
                        {URGENCY_LABEL[r.urgency] ?? r.urgency}
                      </span>
                    </>
                  )}
                  {r.createdAt && (
                    <>
                      <span className="text-gray-500 font-medium whitespace-nowrap">
                        Created
                      </span>
                      <span className="text-gray-800">
                        {new Date(r.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </>
                  )}
                </div>

                {/* Match attempt history */}
                {attempts.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">
                      {attempts.length} attempt
                      {attempts.length !== 1 ? "s" : ""}
                    </p>
                    <div className="space-y-1">
                      {attempts.map((a, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs"
                        >
                          <span className="text-gray-400">#{i + 1}</span>
                          <span>
                            {a.specialistName ??
                              a.providerName ??
                              `Provider #${a.specialistProviderId ?? a.providerId}`}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded text-xs ${
                              (a.status ?? a.result) === "DECLINED"
                                ? "bg-red-100 text-red-600"
                                : "bg-green-100 text-green-600"
                            }`}
                          >
                            {a.status ?? a.result}
                          </span>
                          {(a.declineReason ?? a.reason) && (
                            <span className="text-gray-400">
                              — {a.declineReason ?? a.reason}
                            </span>
                          )}
                          {a.respondedAt && (
                            <span className="text-gray-400">
                              {new Date(a.respondedAt).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                },
                              )}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {r.specialistNote && (
                  <div className="px-3 py-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                    <span className="font-semibold">Specialist note:</span>{" "}
                    {r.specialistNote}
                  </div>
                )}

                {r.status === "MANUAL_REVIEW" && (
                  <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
                    Specialist accepted but has no open slots — our care team is
                    working on scheduling.
                  </div>
                )}

                {declinedView && isDeclinedReferral(r) && (
                  <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    {declineMessage(r)}
                  </div>
                )}

                {r.status === "REMATCHED" && r.previousSpecialistName && (
                  <div className="flex items-center gap-1 text-sm text-indigo-600">
                    <RefreshCw size={11} />
                    Re-matched from Dr. {r.previousSpecialistName}
                  </div>
                )}

                {/* Mark Appointment Scheduled — external referrals only */}
                {canMarkScheduled && (
                  <div className="flex justify-end pt-1">
                    <button
                      onClick={() => handleMarkScheduled(r.referralId)}
                      disabled={isMarkingScheduled}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-40 transition"
                    >
                      {isMarkingScheduled
                        ? "Updating..."
                        : "Mark Appointment Scheduled"}
                    </button>
                  </div>
                )}

                {r.status === "DECLINED" &&
                  r.routingMode === "PROVIDER_DIRECTED" &&
                  onResend && (
                    <div className="flex justify-end pt-1">
                      <button
                        onClick={() => onResend(r)}
                        className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Resend To Another Provider
                      </button>
                    </div>
                  )}

                {r.status === "CANCELLED" && r.cancellationReason && (
                  <p className="text-sm text-red-600 mt-1">
                    <span className="font-medium">Cancellation reason:</span>{" "}
                    {r.cancellationReason}
                  </p>
                )}

                <ReferralDocumentsSection referralId={r.referralId} />

                {(canCancel || canClose) && (
                  <div className="flex justify-end pt-1">
                    {isConfirming ? (
                      canClose ? (
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-600">
                            Close this referral?
                          </span>
                          <button
                            onClick={() => handleClose(r.referralId)}
                            disabled={isClosing}
                            className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40"
                          >
                            {isClosing ? "Processing..." : "Yes, Close"}
                          </button>
                          <button
                            onClick={() =>
                              setConfirming((c) => ({
                                ...c,
                                [r.referralId]: false,
                              }))
                            }
                            className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
                          >
                            Never mind
                          </button>
                        </div>
                      ) : (
                        <div className="w-full space-y-2">
                          <p className="text-xs text-gray-600">
                            Cancel this referral? This cannot be undone.
                          </p>
                          <textarea
                            className="w-full border rounded px-3 py-2 text-sm"
                            rows={2}
                            placeholder="Reason for cancellation (required)..."
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setConfirming((c) => ({
                                  ...c,
                                  [r.referralId]: false,
                                }));
                                setCancelReason("");
                              }}
                              className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
                            >
                              Never mind
                            </button>
                            <button
                              onClick={() => handleCancel(r.referralId)}
                              disabled={isClosing || !cancelReason.trim()}
                              className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40"
                            >
                              {isClosing ? "Cancelling..." : "Yes, Cancel"}
                            </button>
                          </div>
                        </div>
                      )
                    ) : (
                      <button
                        onClick={() =>
                          setConfirming((c) => ({ ...c, [r.referralId]: true }))
                        }
                        className="text-xs px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 text-gray-700"
                      >
                        {canClose ? "Close Referral" : "Cancel Referral"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {closedReferrals.length > 0 && (
        <div className="pt-2 text-center">
          <button
            onClick={() => setShowClosed((s) => !s)}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            {showClosed
              ? "Hide closed referrals"
              : `Show ${closedReferrals.length} closed referral${closedReferrals.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      )}
    </div>
  );
}
